import Head from 'next/head';
import styled from 'styled-components';

import theme from '../../assets/theme';
import Markdown from '../../components/_molecules/Markdown';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import CoreAPIService from '../../services/CoreAPIService';
import { BlogPost } from '../../services/CoreAPIService/CoreAPIService';

const Page = ({ markdown, post }: { markdown: string; post: BlogPost }) => {
  return (
    <>
      <LandingPageLayout
        title={post.title}
        subTitle=""
        primaryActionText="Sign up - It's free"
        hideActions
      >
        <MDContainer>
          <Markdown>{markdown}</Markdown>
        </MDContainer>
      </LandingPageLayout>
      <Head>
        <meta name="description" content={post.description} key="namedescription" />
        <meta property="description" content={post.description} key="description" />
        <meta property="og:description" content={post.description} key="og:description" />
        <meta name="twitter:description" content={post.description} />
        <meta property="og:image" content={post.cover} key="imageurl" />
        <meta property="og:image:secure_url" content={post.cover} key="imagesecureurl" />
        <meta property="og:image:type" content={'image/jpeg'} key="imagetype" />
        <meta property="og:image:alt" content={post.description} key="imageAlt" />
        <meta property="og:image:width" content={'300'} key="imageWidth" />
        <meta property="og:image:height" content={'190'} key="imageHeight" />
        <meta name="twitter:image" content={post.cover} />
      </Head>
    </>
  );
};

const MDContainer = styled.article`
  max-width: 1000px;
  height: 100%;
  padding: 100px 0px;
  color: ${theme.colors.black};
  margin-left: auto;
  margin-right: auto;

  img {
    max-width: 100%;
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
    border-radius: 5px;
  }

  h1 {
    font-weight: bold;
    font-size: 60px;
    margin-top: 80px;
    @media ${theme.device.mobileL} {
      font-size: 40px;
    }
  }
  h2 {
    font-weight: bold;
    font-size: 35px;
    margin-bottom: 40px;
    margin-top: 60px;
  }
  h3 {
    font-weight: bold;
    font-size: 28px;
    margin-bottom: 40px;
    margin-top: 60px;
  }

  p {
    font-size: 22px;
    line-height: 34px;
    font-weight: 450;
  }

  li {
    font-size: 22px;
    line-height: 34px;
    font-weight: 450;
  }

  a {
    font-weight: 450;
    font-size: 22px;
    color: #5e5e5e;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    text-decoration: underline;
    word-break: break-word;
    &:hover {
      color: #1e1e1e;
    }
  }

  @media ${theme.device.laptop} {
    width: 680px;
  }
  @media ${theme.device.tablet} {
    width: 400px;
  }
  @media ${theme.device.mobileL} {
    width: 300px;
  }
`;

export async function getStaticProps({
  params: { pageId },
}: {
  params: { pageId: string };
}) {
  const res = await CoreAPIService.fetchNotionSingleBlogPost(pageId);
  return {
    props: {
      markdown: res.markdown,
      post: res.post,
    },
  };
}

export async function getStaticPaths() {
  const posts = await CoreAPIService.fetchNotionBlogposts();

  // Because we are generating static paths, you will have to redeploy your site whenever
  // you make a change in Notion.
  const paths = posts.map(post => {
    return {
      params: {
        pageId: post.slug,
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
}

export default Page;
